import { useState, useEffect, useMemo } from 'react';
import { getCompanies, getBankStatementData, saveBankChanges } from '../services/api';
import { Company, BankStatementData } from '../types/api';
import SpreadsheetEditor, { CellChangeEvent, SpreadsheetColumn, SpreadsheetMeta } from './SpreadsheetEditor';

export default function BankStatements() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [data, setData] = useState<BankStatementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    account_id: undefined as number | undefined,
    date_from: '',
    date_to: '',
    transaction_type: '',
    currency: ''
  });

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      loadData();
    }
  }, [selectedCompany, filters]);

  const loadCompanies = async () => {
    try {
      const result = await getCompanies();
      setCompanies(result);
      if (result.length > 0) {
        setSelectedCompany(result[0].id);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadData = async () => {
    if (!selectedCompany) return;
    setLoading(true);
    try {
      const result = await getBankStatementData(selectedCompany, {
        account_id: filters.account_id,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        transaction_type: filters.transaction_type || undefined,
        currency: filters.currency || undefined
      });
      setData(result);
    } catch (error) {
      console.error('Failed to load bank data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSpreadsheetApprove = async (rowIndex: number, approved: boolean) => {
    if (!data) return;
    const row = data.data[rowIndex];
    const transactionId = row[3] as number;
    const currentStatus = row[0] as string;
    const newStatus = approved ? 'Approved' : 'Approve';

    try {
      await saveBankChanges([{
        transaction_id: transactionId,
        field: 'approval_status',
        old_value: currentStatus,
        new_value: newStatus
      }]);
      loadData();
    } catch (error) {
      console.error('Failed to update approval:', error);
    }
  };

  const handleSpreadsheetCellChange = async (event: CellChangeEvent) => {
    if (!data) return;
    const row = data.data[event.row];
    const transactionId = row[3] as number;

    const columnMap: Record<number, string> = {
      2: 'nominal_code',
      4: 'transaction_date',
      5: 'value_date',
      6: 'account_name',
      7: 'currency',
      8: 'description',
      9: 'reference',
      10: 'debit',
      11: 'credit',
    };

    const fieldName = columnMap[event.col];
    if (!fieldName) return;

    try {
      await saveBankChanges([{
        transaction_id: transactionId,
        field: fieldName,
        old_value: String(event.oldValue || ''),
        new_value: String(event.newValue || '')
      }]);
      loadData();
    } catch (error) {
      console.error('Failed to save change:', error);
    }
  };

  // Prepare spreadsheet data
  const spreadsheetData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((row) => [
      row[0],  // Approve
      row[1],  // Files
      row[2],  // Nominal
      row[3],  // ID
      row[4],  // Date
      row[6],  // Account
      row[7],  // Currency
      row[8],  // Description
      row[9],  // Reference
      row[10], // Debit
      row[11], // Credit
      row[12], // Balance
    ]);
  }, [data?.data]);

  const spreadsheetMetadata = useMemo((): SpreadsheetMeta[] => {
    if (!data?.data || !data?.metadata) return [];
    return data.metadata.map((meta, index) => ({
      row: index,
      hasEdits: meta.has_edits || false,
      transactionId: meta.transaction_id,
    }));
  }, [data?.data, data?.metadata]);

  const spreadsheetColumns: SpreadsheetColumn[] = [
    { title: 'Approve', width: 80, type: 'text', readOnly: true },
    { title: 'Files', width: 50, type: 'text', readOnly: true, align: 'center' },
    { title: 'Nominal', width: 80, type: 'text' },
    { title: 'ID', width: 60, type: 'text', readOnly: true },
    { title: 'Date', width: 90, type: 'calendar' },
    { title: 'Account', width: 120, type: 'text', readOnly: true },
    { title: 'Currency', width: 70, type: 'dropdown', source: data?.currencies || ['USD', 'EUR', 'GBP'] },
    { title: 'Description', width: 250, type: 'text' },
    { title: 'Reference', width: 100, type: 'text' },
    { title: 'Debit', width: 100, type: 'numeric', align: 'right', decimal: '.' },
    { title: 'Credit', width: 100, type: 'numeric', align: 'right', decimal: '.' },
    { title: 'Balance', width: 100, type: 'numeric', align: 'right', decimal: '.', readOnly: true },
  ];

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <h1 className="text-xl font-bold text-gray-900 mb-4">Bank Statements</h1>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={selectedCompany || ''}
            onChange={(e) => setSelectedCompany(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.company_name}
              </option>
            ))}
          </select>

          {data?.accounts && data.accounts.length > 0 && (
            <select
              value={filters.account_id || ''}
              onChange={(e) => setFilters({ ...filters, account_id: e.target.value ? Number(e.target.value) : undefined })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Accounts</option>
              {data.accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          )}

          {data?.currencies && data.currencies.length > 0 && (
            <select
              value={filters.currency}
              onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Currencies</option>
              {data.currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          )}

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="From"
          />

          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="To"
          />

          <select
            value={filters.transaction_type}
            onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
        </div>
      </div>

      {/* Data table */}
      <div className="flex-1 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p>No bank transactions found</p>
          </div>
        ) : (
          <SpreadsheetEditor
            data={spreadsheetData}
            columns={spreadsheetColumns}
            metadata={spreadsheetMetadata}
            onCellChange={handleSpreadsheetCellChange}
            onApprove={handleSpreadsheetApprove}
            height="100%"
            highlightEdited={true}
            approveColumn={0}
          />
        )}
      </div>

      {/* Summary */}
      {data && data.data.length > 0 && (
        <div className="bg-white border-t px-6 py-3 flex items-center justify-between text-sm text-gray-600">
          <span>{data.data.length} transactions</span>
          <div className="flex items-center space-x-4">
            <span className="text-red-600">
              Total Debit: {data.data.reduce((sum, row) => sum + (Number(row[10]) || 0), 0).toFixed(2)}
            </span>
            <span className="text-green-600">
              Total Credit: {data.data.reduce((sum, row) => sum + (Number(row[11]) || 0), 0).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
